import { sortBy } from "lodash-es";
import {
  AssemblyAILegacyV2Message,
  AssemblyAIMessage,
  SerializedAssemblyAITurnMessage,
  TimestampedAssemblyAITurnMessage,
} from "@/domains/dexie-db/audio-capture/types";

export const mapLegacyV2TranscriptToV3Transcript = (
  transcript: AssemblyAILegacyV2Message,
  turnOrder: number
): TimestampedAssemblyAITurnMessage => {
  return {
    type: "Turn",
    turn_order: turnOrder,
    turn_is_formatted: true,
    end_of_turn: true,
    transcript: transcript.text,
    end_of_turn_confidence: transcript.confidence,
    words: [],
    timestamp: transcript.audio_start,
  };
};

/**
 * Normalizes a list of transcripts (V2 and V3) to a Map of turn_order to V3 transcripts.
 * For V2 transcripts, maps unique audio_start values to increasing turn_order indices.
 * For V3 transcripts, uses their existing turn_order.
 */
export const normalizeTranscriptsToV3Map = (
  transcripts: AssemblyAIMessage[]
): Map<number, TimestampedAssemblyAITurnMessage> => {
  const map = new Map<number, TimestampedAssemblyAITurnMessage>();

  // Separate V2 and V3 transcripts
  const v2Transcripts = transcripts.filter(t => "audio_start" in t) as AssemblyAILegacyV2Message[];
  const v3Transcripts = transcripts.filter(
    t => "turn_order" in t && t.words.length > 0
  ) as SerializedAssemblyAITurnMessage[];

  // Sort V2 transcripts by audio_start and V3 transcripts by turn_order
  const sortedV2Transcripts = sortBy(v2Transcripts, "audio_start");
  const sortedV3Transcripts: TimestampedAssemblyAITurnMessage[] = [];
  sortBy(v3Transcripts, "turn_order").forEach((t: SerializedAssemblyAITurnMessage) => {
    const timestamp =
      t.timestamp ??
      // Not perfect when there are multiple channels due to potentially different word timestamps.
      // But that shouldn't be a problem as no-one else (but me) had access to multiple channels with v3 before.
      t.words[t.words.length - 1].start;
    sortedV3Transcripts.push({ ...t, timestamp });
  });

  // Create a mapping from audio_start to index for V2 transcripts
  // Handle duplicates by using the first occurrence of each audio_start
  const audioStartToIndex = new Map<number, number>();
  let v2Index = 0;

  sortedV2Transcripts.forEach((transcript: AssemblyAILegacyV2Message) => {
    if (!audioStartToIndex.has(transcript.audio_start)) {
      audioStartToIndex.set(transcript.audio_start, v2Index);
      v2Index++;
    }
    const turnOrder = audioStartToIndex.get(transcript.audio_start)!;
    const v3Transcript = mapLegacyV2TranscriptToV3Transcript(transcript, turnOrder);
    map.set(turnOrder, v3Transcript);
  });

  // Handle V3 transcripts with their original turn_order values
  sortedV3Transcripts.forEach((transcript: TimestampedAssemblyAITurnMessage) => {
    const adjustedTurnOrder = transcript.turn_order + v2Index;
    map.set(adjustedTurnOrder, { ...transcript, turn_order: adjustedTurnOrder });
  });

  return map;
};
